import { useEffect, useMemo, useState } from 'react';
import type { Contractor, Product } from '@bpd/shared';
import { formatCurrency } from '@bpd/shared';
import { projectsApi } from '../../api/projects.ts';
import { CurrencyInput } from '../../components/CurrencyInput.tsx';
import { ContractorPicker } from '../../components/projects/ContractorPicker.tsx';
import { SearchWidget } from '../../components/search/SearchWidget.tsx';

type LoadState = 'loading' | 'ready' | 'error' | 'not-configured';

// Local helper server from scripts/homedepot-lookup/server.mjs -- always
// localhost regardless of which environment (dev/prod) this page is loaded
// from, since the helper runs on the admin's own Mac, not in AWS. Not
// configurable via env because there's nothing environment-specific about
// it; see that script's header comment for what it does and why it exists
// (homedepot.com blocks plain server-side fetches, so a real local Chrome
// has to do the lookup).
const HOMEDEPOT_LOOKUP_SERVER = 'http://localhost:8787';

interface HomeDepotLookupResult {
  item?: string;
  sku?: string;
  unitPrice?: number;
  description?: string;
  warnings?: string[];
}

function formatDateOnly(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

export function ProductsPage() {
  const [state, setState] = useState<LoadState>('loading');
  const [products, setProducts] = useState<Product[]>([]);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [item, setItem] = useState('');
  const [description, setDescription] = useState('');
  const [sku, setSku] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [contractorId, setContractorId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPaste, setShowPaste] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteError, setPasteError] = useState<string | null>(null);
  const [lookupUrl, setLookupUrl] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupWarnings, setLookupWarnings] = useState<string[]>([]);

  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => [p.item, p.sku, p.description].some((f) => f && f.toLowerCase().includes(q)));
  }, [products, searchQuery]);

  function load() {
    if (!projectsApi.isConfigured()) {
      setState('not-configured');
      return;
    }
    setState('loading');
    Promise.all([projectsApi.listProducts(), projectsApi.listContractors()])
      .then(([productData, contractorData]) => {
        setProducts(productData);
        setContractors(contractorData);
        setState('ready');
      })
      .catch(() => setState('error'));
  }

  useEffect(load, []);

  function resetForm() {
    setItem('');
    setDescription('');
    setSku('');
    setUnitPrice('');
    setContractorId('');
    setShowAdd(false);
    setEditingId(null);
    setError(null);
    setShowPaste(false);
    setPasteText('');
    setPasteError(null);
    setLookupUrl('');
    setLookupError(null);
    setLookupWarnings([]);
  }

  function applyLookupResult(parsed: HomeDepotLookupResult) {
    if (typeof parsed.item === 'string') setItem(parsed.item);
    if (typeof parsed.sku === 'string') setSku(parsed.sku);
    if (typeof parsed.unitPrice === 'number') setUnitPrice(String(parsed.unitPrice));
    if (typeof parsed.description === 'string') setDescription(parsed.description);
  }

  function fillFromLookupJson(text: string): boolean {
    setPasteError(null);
    let parsed: HomeDepotLookupResult;
    try {
      parsed = JSON.parse(text) as HomeDepotLookupResult;
    } catch {
      setPasteError('Not valid JSON — paste the full output from homedepot-lookup/lookup.mjs.');
      return false;
    }
    applyLookupResult(parsed);
    setShowPaste(false);
    setPasteText('');
    return true;
  }

  function handleFillFromPaste() {
    fillFromLookupJson(pasteText);
  }

  async function handleReadClipboard() {
    setPasteError(null);
    try {
      const text = await navigator.clipboard.readText();
      if (!text.trim()) {
        setPasteError('Clipboard is empty — run lookup.mjs first, it copies its JSON output automatically.');
        return;
      }
      if (!fillFromLookupJson(text)) setPasteText(text);
    } catch {
      setPasteError('Could not read the clipboard (browser permission denied) — paste manually below instead.');
    }
  }

  async function handleLookupUrl() {
    const trimmed = lookupUrl.trim();
    setLookupError(null);
    setLookupWarnings([]);
    if (!trimmed.includes('homedepot.com')) {
      setLookupError('That doesn’t look like a homedepot.com product URL.');
      return;
    }
    setLookupLoading(true);
    try {
      const res = await fetch(`${HOMEDEPOT_LOOKUP_SERVER}/lookup?url=${encodeURIComponent(trimmed)}`);
      const body: unknown = await res.json().catch(() => null);
      if (!res.ok) {
        const message =
          body && typeof body === 'object' && 'error' in body ? String(body.error) : `Lookup failed (${res.status})`;
        setLookupError(message);
        return;
      }
      const result = body as HomeDepotLookupResult;
      applyLookupResult(result);
      setLookupWarnings(result.warnings ?? []);
      setLookupUrl('');
    } catch {
      setLookupError(
        'Could not reach the local lookup helper. Start it with: cd scripts/homedepot-lookup && node server.mjs',
      );
    } finally {
      setLookupLoading(false);
    }
  }

  function startEdit(p: Product) {
    setEditingId(p.id);
    setItem(p.item);
    setDescription(p.description ?? '');
    setSku(p.sku ?? '');
    setUnitPrice(String(p.unitPrice));
    setContractorId(p.contractorId ?? '');
    setShowAdd(true);
  }

  function handleContractorCreated(contractor: Contractor) {
    setContractors((prev) => [...prev, contractor].sort((a, b) => a.name.localeCompare(b.name)));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const price = Number(unitPrice);
    if (!item.trim() || !Number.isFinite(price) || price < 0) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        item: item.trim(),
        ...(description ? { description } : {}),
        ...(sku ? { sku } : {}),
        unitPrice: price,
        ...(contractorId ? { contractorId } : {}),
      };
      if (editingId) {
        const updated = await projectsApi.updateProduct(editingId, payload);
        setProducts((prev) =>
          prev
            .map((p) => (p.id === editingId ? updated : p))
            .sort((a, b) => a.item.localeCompare(b.item)),
        );
      } else {
        const created = await projectsApi.createProduct(payload);
        setProducts((prev) => [...prev, created].sort((a, b) => a.item.localeCompare(b.item)));
      }
      resetForm();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    await projectsApi.deleteProduct(id);
    setProducts((prev) => prev.filter((p) => p.id !== id));
    setConfirmDeleteId(null);
  }

  return (
    <main className="mx-auto max-w-4xl px-6 py-16">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-3xl font-bold text-phoenix-blue-900">Products</h1>
        <button
          type="button"
          onClick={() => (showAdd ? resetForm() : setShowAdd(true))}
          className="rounded-md bg-phoenix-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-phoenix-blue-700"
        >
          {showAdd ? 'Cancel' : '+ Add product'}
        </button>
      </div>
      <p className="mt-2 text-sm text-gray-500">
        Reusable across every client and project — pick from this list when adding a purchase to a
        project.
      </p>

      <div className="mt-4">
        <SearchWidget categories={['products']} onQueryChange={setSearchQuery} />
      </div>

      {showAdd && (
        <form
          onSubmit={(e) => void handleSubmit(e)}
          className="mt-6 space-y-3 rounded-lg border border-gray-200 bg-white p-5 shadow-sm"
        >
          <div className="space-y-2 rounded-md border border-gray-200 bg-gray-50 p-3">
            <p className="text-xs font-medium text-gray-700">Add from Home Depot</p>
            <div className="flex gap-2">
              <input
                type="url"
                placeholder="Paste a homedepot.com product URL"
                value={lookupUrl}
                onChange={(e) => setLookupUrl(e.target.value)}
                className="flex-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              />
              <button
                type="button"
                onClick={() => void handleLookupUrl()}
                disabled={lookupLoading || !lookupUrl.trim()}
                className="shrink-0 rounded-md bg-phoenix-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
              >
                {lookupLoading ? 'Looking up…' : 'Look up'}
              </button>
            </div>
            <p className="text-xs text-gray-400">
              Needs the local helper running once:{' '}
              <code>cd scripts/homedepot-lookup &amp;&amp; node server.mjs</code>
            </p>
            {lookupError && <p className="text-xs text-red-600">{lookupError}</p>}
            {lookupWarnings.length > 0 && (
              <ul className="list-disc space-y-0.5 pl-4 text-xs text-amber-700">
                {lookupWarnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            )}
            <button
              type="button"
              onClick={() => setShowPaste((v) => !v)}
              className="text-xs font-medium text-phoenix-blue-600 hover:underline"
            >
              {showPaste ? 'Hide' : "Helper not running? Paste lookup.mjs output manually"}
            </button>
            {showPaste && (
              <div className="space-y-1.5 rounded-md border border-gray-200 bg-white p-3">
                <p className="text-xs text-gray-500">
                  Run <code>node scripts/homedepot-lookup/lookup.mjs &quot;&lt;product URL&gt;&quot;</code> —
                  it copies its JSON output to your clipboard automatically.
                </p>
                <button
                  type="button"
                  onClick={() => void handleReadClipboard()}
                  className="rounded-md bg-phoenix-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-phoenix-blue-700"
                >
                  Fill from clipboard
                </button>
                <p className="text-xs text-gray-400">Or paste the JSON manually:</p>
                <textarea
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  rows={4}
                  placeholder='{"item": "...", "sku": "...", "unitPrice": 12.99, "description": "..."}'
                  className="w-full rounded-md border border-gray-300 px-2 py-1.5 font-mono text-xs"
                />
                {pasteError && <p className="text-xs text-red-600">{pasteError}</p>}
                <button
                  type="button"
                  onClick={handleFillFromPaste}
                  disabled={!pasteText.trim()}
                  className="rounded-md bg-phoenix-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
                >
                  Fill fields
                </button>
              </div>
            )}
          </div>
          <input
            type="text"
            placeholder="Item *"
            value={item}
            onChange={(e) => setItem(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            maxLength={200}
            required
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              placeholder="SKU"
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            />
            <CurrencyInput
              value={unitPrice}
              onChange={setUnitPrice}
              placeholder="Unit price *"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500">Vendor (optional)</label>
            <div className="mt-0.5">
              <ContractorPicker
                contractors={contractors}
                value={contractorId}
                onChange={setContractorId}
                onContractorCreated={handleContractorCreated}
                label="vendor"
              />
            </div>
          </div>
          <textarea
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            maxLength={4000}
          />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button
            type="submit"
            disabled={saving}
            className="rounded-md bg-phoenix-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-phoenix-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : editingId ? 'Save changes' : 'Add product'}
          </button>
        </form>
      )}

      {state === 'not-configured' && (
        <p className="mt-8 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          The project management backend isn&apos;t connected yet in this environment.
        </p>
      )}
      {state === 'error' && (
        <p className="mt-8 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Couldn&apos;t load products. Try again in a moment.
        </p>
      )}
      {state === 'loading' && <p className="mt-8 text-sm text-gray-500">Loading…</p>}

      {state === 'ready' && (
        <div className="mt-6 space-y-2">
          {products.length === 0 && (
            <p className="text-sm text-gray-400">No products yet — add one above.</p>
          )}
          {products.length > 0 && filteredProducts.length === 0 && (
            <p className="text-sm text-gray-400">No products match &quot;{searchQuery.trim()}&quot;.</p>
          )}
          {filteredProducts.map((p) => (
            <div key={p.id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-gray-900">
                    {p.item}
                    {p.sku && (
                      <span className="ml-2 text-sm font-normal text-gray-500">SKU: {p.sku}</span>
                    )}
                  </p>
                  <p className="text-sm text-gray-500">
                    {formatCurrency(p.unitPrice)} / unit
                    <span className="text-gray-400">
                      {' '}
                      (as of {formatDateOnly(p.priceAsOf ?? p.createdAt)})
                    </span>
                    {p.contractorId && (
                      <>
                        {' '}
                        —{' '}
                        {contractors.find((c) => c.id === p.contractorId)?.name ?? 'Unknown vendor'}
                      </>
                    )}
                  </p>
                  {p.description && <p className="mt-1 text-sm text-gray-600">{p.description}</p>}
                </div>
                {confirmDeleteId !== p.id ? (
                  <div className="flex shrink-0 items-center gap-3">
                    <button
                      type="button"
                      onClick={() => startEdit(p)}
                      className="text-sm text-gray-600 hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(p.id)}
                      className="text-sm text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </div>
                ) : (
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={() => void handleDelete(p.id)}
                      className="rounded-md bg-red-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-red-700"
                    >
                      Confirm
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(null)}
                      className="text-xs text-gray-500 hover:underline"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
